/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Col,
  Row,
  Modal,
  Button,
  Spin,
  Grid,
  Form,
  Typography,
  DatePicker,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import Moment from 'moment';
import FormSubmitControls from 'components/FormSubmitCustomControls';
import Table from 'components/Table';
import currencyFormat from 'utils/toCurrency';
import NumericInput from 'components/NumericInput';
import { createDecimalRegExp } from 'utils/patterns';
import getFormatedValues from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Select from 'components/Select';
import { getTiposDeAplicacionParaCargos, postGenerarCargos } from 'api/recaudacion/index';
import { getTipoDeCargo, getTiposDeCargos } from 'api/recaudacion/tiposDeCargo';
import { getVariable, getVariables } from 'api/configuracion/variables';
import { getTarifas } from 'api/configuracion/tarifas';
import { postRecalcularCargo } from 'api/recaudacion/cargos';
import { ESTADOS_GLOBALES_MAP } from 'utils/estadosGlobales';
import { getCatalogoDeTarifa } from 'api/configuracion/catalogoDeTarifas';
import {
  aplicacionesCargosEnum,
  tiposDatosFormulaEnum,
  tiposDatosEnum,
} from 'views/Tramites/GPM/enums';
import Variables from 'views/Tramites/GPM/components/Variables';
import { getValoresPorVariablesDeSeleccion } from 'api/configuracion/valoresPorVariablesDeSeleccion';
import { getCategoriasDeVariables } from 'api/configuracion';

const { useBreakpoint } = Grid;
const { Text } = Typography;

const tiposDeTarifasEnum = Object.freeze({
  PORCENTAJE: 1,
  RANGO: 2,
  SIMPLE: 3,
  CONCEPTO: 4,
  CONCEPTO_MULTIPLO: 5,
});

const monedasEnum = Object.freeze({
  PESO: 1,
  LEMPIRA: 2,
  SM: 3,
  UMA: 4,
});

const ModalAgregarCargos = ({
  callback,
  _cargo,
  recalculate,
  visible,
  padron,
  onCancel: _onCancel,
}) => {
  const screen = useBreakpoint();
  const user = useSelector(({ auth }) => auth.user);
  const [form] = Form.useForm();
  const [formCantidad] = Form.useForm();
  const monedaDefault = useSelector(({ auth }) => auth.monedaDefault);
  const [decimal] = createDecimalRegExp(monedaDefault);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [tipoAplicacion, setTipoAplicacion] = useState(0);
  const [hideTable, setHideTable] = useState(false);
  const [selectedCargo, setSelectedCargo] = useState({});
  const [variables, setVariables] = useState([]);
  const [conceptos, setConceptos] = useState({ results: [], count: 0 });
  const [tiposDeAplicacionCargos, setTipoDeAplicacionCargos] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [esTarifaPorConcepto, setEsTarifaPorConcepto] = useState(false);
  const conceptosRef = useRef({ results: [], count: 0 });
  const concepto = Form.useWatch('concepto', form);

  const [catalogoDeVariables, setCatalogoDeVariables] = useState([]);
  const [categoriasDeVariables, setCategoriasDeVariables] = useState([]);
  const [variablesOptions, setVariablesOptions] = useState([]);

  const onCancel = () => {
    setTipoAplicacion(0);
    setSelectedCargo({});
    if (hideTable) {
      form.resetFields();
      formCantidad.resetFields();
    }
    setHideTable(false);
    setEsTarifaPorConcepto(false);
    setSelectedRowKeys([]);
    setConceptos({ results: [], count: 0 });
    _onCancel();
  };

  const getConceptos = async (params = null) => {
    setLoading(true);
    const _catalogoDeTarifa = await getCatalogoDeTarifa(params.catalogo_de_tarifa);
    setEsTarifaPorConcepto([tiposDeTarifasEnum.CONCEPTO_MULTIPLO, tiposDeTarifasEnum.CONCEPTO]
      .includes(_catalogoDeTarifa.tipo_de_tarifa));
    const dataConceptos = await getTarifas({ catalogo_de_tarifa: _catalogoDeTarifa.id });
    setConceptos(dataConceptos);
    setLoading(false);
  };

  const fetchCargos = async (page = 1, params = null) => {
    setLoading(true);
    setSelectedRowKeys([]);
    if (!recalculate) {
      setCurrentPage(page);
      const padronCat = padron.tipo_de_padron || padron.padron;
      const _params = {
        padron: padronCat,
        es_accesorio: false,
        canales_de_pago: user.canal_de_pago,
        estados_globales_filter: `${ESTADOS_GLOBALES_MAP.ACTIVO}, ${ESTADOS_GLOBALES_MAP.AUTORIZADO}`,
        no_cargar_manualmente: false,
      };
      const _data = await getTiposDeCargos({ page, ...params, ..._params });
      setData(_data);
    }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    if (padron && visible) {
      const fetchData = async () => {
        setLoading(true);
        const [_tiposDeAplicaciones] = await Promise.all([
          getTiposDeAplicacionParaCargos(),
          fetchCargos(1, {
            padron,
            es_accesorio: false,
            canales_de_pago: user.canal_de_pago,
          }),
        ]);
        if (mounted) {
          setTipoDeAplicacionCargos(_tiposDeAplicaciones);
          setLoading(false);
        }
      };
      if (padron.id) {
        fetchData();
      }
    }
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [padron, visible]);

  useEffect(() => {
    const recalcularCargo = async () => {
      setLoading(true);
      const tipoDeCargoId = _cargo.tipo_de_cargo.id;
      const _tipoDeCargo = await getTipoDeCargo(tipoDeCargoId);
      setData({ results: [], count: 1 });
      if (_tipoDeCargo.tipo_de_aplicacion === aplicacionesCargosEnum.FIJO) {
        onCancel();
        message.warn('No se puede recalcular un cargo de tipo de aplicación "Fijo"');
      } else {
        setSelectedRowKeys([tipoDeCargoId]);
        if (hideTable) {
          // eslint-disable-next-line no-use-before-define
          await agregarCargo(_tipoDeCargo);
        } else {
          // eslint-disable-next-line no-use-before-define
          await onFinishSearch(_tipoDeCargo);
        }
      }
      setLoading(false);
    };
    if (_cargo && recalculate && visible) {
      setTimeout(recalcularCargo);
    }
  }, [_cargo, visible, recalculate]);

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const requiredRule = { required: true };

  const rules = {
    required: [requiredRule],
    importe: [
      {
        validator: async (_, val) => {
          if (val && !decimal.test(val)) {
            throw new Error('Ingrese un valor decimal válido');
          }
          if (val < selectedCargo?.importe_minimo) {
            throw new Error(`Ingrese una cantidad mayor al importe mínimo del cargo (${selectedCargo?.importe_minimo})`);
          }
          if (val > selectedCargo?.importe_maximo) {
            throw new Error(`Ingrese una cantidad menor al importe máximo del cargo (${selectedCargo?.importe_maximo})`);
          }
        },
      },
    ],
  };
  const fijos = [aplicacionesCargosEnum.FIJO, aplicacionesCargosEnum.MULTIPLO];
  const capturables = [aplicacionesCargosEnum.CAPTURABLE, aplicacionesCargosEnum.PORCENTAJE];

  const notAllowedCapturableZero = capturables.includes(selectedCargo?.tipo_de_aplicacion)
  && !selectedCargo?.permite_importe_en_cero;

  const notAllowedZero = (fijos.includes(selectedCargo?.tipo_de_aplicacion))
      && selectedCargo.importe === 0 && !selectedCargo.permite_importe_en_cero;

  const calcularImportePorTarifa = (cargo, values) => {
    switch (cargo.tipo_de_dato) {
      case tiposDatosEnum.ENTERO:
        return values.importe;
      case tiposDatosEnum.DECIMAL:
        return parseFloat(values.importe).toFixed(2);
      case tiposDatosEnum.RANGO_DE_FECHAS: {
        const { fecha_inicial, fecha_final } = values;
        return new Moment(fecha_final).diff(new Moment(fecha_inicial), 'days');
      }
      default:
        return values.importe;
    }
  };

  const calcularVariables = (cargo, values) => {
    if (![aplicacionesCargosEnum.FORMULA, aplicacionesCargosEnum.SQL_FUNCTION]
      .includes(cargo.tipo_de_aplicacion)) {
      return null;
    }

    return cargo.variables.map((e) => {
      const match = variables.find((item) => item.id === e);
      let valor = values[match.nombre_de_variable];

      if (match.tipo_de_variable === tiposDatosFormulaEnum.RANGO_DE_FECHAS) {
        const [fechaInicial, fechaFinal] = valor;
        valor = fechaFinal.diff(fechaInicial, 'days');
      }

      return {
        id: e,
        nombre_de_variable: match.nombre_de_variable,
        valor,
      };
    });
  };

  const calcularImporte = (cargo, values) => {
    switch (cargo.tipo_de_aplicacion) {
      case aplicacionesCargosEnum.CAPTURABLE:
        return values.importe;
      case aplicacionesCargosEnum.FIJO:
        return parseFloat(cargo.importe, 10);
      case aplicacionesCargosEnum.MULTIPLO:
        return parseFloat(values.importe);
      case aplicacionesCargosEnum.TARIFA:
        return calcularImportePorTarifa(cargo, values);
      default:
        return values.importe;
    }
  };

  const enviarCargo = async (formatedValues, times) => (!_cargo?.id && !recalculate
    ? postGenerarCargos(formatedValues, times)
    : postRecalcularCargo(_cargo.id, formatedValues));

  const agregarCargo = async (cargo) => {
    try {
      setLoading(true);
      const { cantidad } = formCantidad.getFieldsValue();
      const times = cantidad || 1;
      const padron_id = padron.id;
      const tipo_de_cargo = cargo.id;
      const values = form.getFieldsValue();
      const isConcept = esTarifaPorConcepto;

      const importe = calcularImporte(cargo, values);
      const vars = calcularVariables(cargo, values);

      let formatedValues = getFormatedValues({
        padron: padron.tipo_de_padron,
        padron_id,
        tipo_de_cargo,
        importe,
        variable: isConcept ? values.variable : undefined,
        fecha_de_vencimiento: values?.fecha_de_vencimiento || undefined,
      });

      if (cargo.tipo_de_aplicacion === aplicacionesCargosEnum.FORMULA) {
        delete formatedValues.importe;
        formatedValues = getFormatedValues({
          ...formatedValues,
          variables: vars,
        });
      }

      if (isConcept) {
        formatedValues = {
          ...formatedValues,
          ...values,
        };
      }

      if ((notAllowedCapturableZero && !values?.importe) || notAllowedZero) {
        message.warn('El cargo seleccionado no permite importe en cero');
        setLoading(false);
        return;
      }

      const result = await enviarCargo(formatedValues, times);

      if (result) {
        formCantidad.setFieldsValue({ cantidad: 1 });
        callback(padron);
        setConceptos({ results: [] });
        onCancel();
      }

      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleFormulaOrSqlFunction = async (record) => {
    if (record?.variables?.length) {
      const [
        _catalogosDeVariables,
        _variables,
        _variablesOptions,
        _categoriasDeVariables,
      ] = await Promise.all([
        getVariables(),
        Promise.all(record.variables.map((e) => getVariable(e))),
        getValoresPorVariablesDeSeleccion(),
        getCategoriasDeVariables(),
      ]);

      setVariables(_variables);
      setCatalogoDeVariables(_catalogosDeVariables);
      setCategoriasDeVariables(_categoriasDeVariables);
      setVariablesOptions(_variablesOptions);
      setHideTable(true);
    } else if (!record.es_editable_fecha_de_vencimiento) {
      agregarCargo(record);
    }
  };

  const onFinishSearch = async (record) => {
    await form.validateFields();
    setTipoAplicacion(record.tipo_de_aplicacion);
    setSelectedCargo(record);

    const _tipoAplicacion = record.tipo_de_aplicacion;

    if (record.es_editable_fecha_de_vencimiento) {
      setHideTable(true);
      form.setFieldsValue({
        fecha_de_vencimiento: record?.fecha_de_vencimiento
          ? Moment(record.fecha_de_vencimiento) : Moment(),
      });
    }

    switch (_tipoAplicacion) {
      case aplicacionesCargosEnum.FIJO:
        if (!record.es_editable_fecha_de_vencimiento) {
          agregarCargo(record);
        }
        break;

      case aplicacionesCargosEnum.CAPTURABLE:
      case aplicacionesCargosEnum.MULTIPLO:
        setHideTable(true);
        break;

      case aplicacionesCargosEnum.FORMULA:
      case aplicacionesCargosEnum.SQL_FUNCTION:
        await handleFormulaOrSqlFunction(record);
        break;

      case aplicacionesCargosEnum.TARIFA:
        setHideTable(true);
        setConceptos({ results: [], count: 0 });
        await getConceptos({ catalogo_de_tarifa: record.catalogo_de_tarifa });
        break;

      default:
        break;
    }
  };

  const renderImporte = (val, record) => {
    const obj = {
      [aplicacionesCargosEnum.CAPTURABLE]: {
        [monedasEnum.PESO]: `${currencyFormat(record?.importe_minimo)} - ${currencyFormat(record?.importe_maximo)}`,
        [monedasEnum.LEMPIRA]: `${currencyFormat(record?.importe_minimo)} - ${currencyFormat(record?.importe_maximo)}`,
        [monedasEnum.SM]: `${record.importe_minimo} SM - ${record.importe_maximo} SM`,
        [monedasEnum.UMA]: `${record.importe_minimo} UMAS - ${record.importe_maximo} UMAS`,
      },
      [aplicacionesCargosEnum.FIJO]: {
        [monedasEnum.PESO]: currencyFormat(val),
        [monedasEnum.LEMPIRA]: currencyFormat(val),
        [monedasEnum.SM]: `${record.importe} SM`,
        [monedasEnum.UMA]: `${record.importe} UMAS`,
      },
    };
    return obj?.[record.tipo_de_aplicacion]?.[record.moneda] || 'Por calcular';
  };

  useEffect(() => {
    formCantidad.setFieldsValue({ cantidad: 1 });
    // eslint-disable-next-line
  }, [selectedCargo]);

  const columns = [
    {
      titleText: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 300,
    },
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 300,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 500,
    },
    {
      titleText: 'Tipo de Aplicación',
      dataIndex: 'tipo_de_aplicacion',
      key: 'tipo_de_aplicacion',
      catalog: {
        cat: tiposDeAplicacionCargos,
        labelProp: 'descripcion',
        use: true,
      },
      width: 200,
    },
    {
      titleText: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      align: 'right',
      render: renderImporte,
      width: 200,
    },
  ];

  const controls = [
    {
      text: 'Cancelar',
      icon: <ArrowLeftOutlined />,
      onClick: () => {
        if (recalculate) {
          onCancel();
        } else if (hideTable) {
          setHideTable(false);
        } else {
          onCancel();
        }
      },
    },
    {
      text: recalculate ? 'Recalcular' : 'Agregar',
      icon: recalculate ? <ReloadOutlined /> : <PlusOutlined />,
      onClick: async () => {
        if (selectedRowKeys.length) {
          const [key] = selectedRowKeys;
          let match = data.results.find((e) => e?.id === key);
          if (!match) {
            match = await getTipoDeCargo(key);
          }
          if (hideTable) {
            try {
              await form.validateFields();
              agregarCargo(match);
            } catch (err) {
              onError(err, setLoading);
            }
          } else {
            onFinishSearch(match);
          }
        } else {
          message.warn('Selecione algún cargo');
        }
      },
    },
  ];

  const getWidthModal = () => {
    if (screen.md) {
      if (!hideTable) {
        return '80%';
      }
      if (tipoAplicacion === aplicacionesCargosEnum.FORMULA && selectedCargo.length > 6) {
        return '80%';
      }
      return '50%';
    }
    return '100%';
  };
  return (
    <Modal
      footer={null}
      closable={false}
      width={getWidthModal()}
      open={visible}
      keyboard={!loading}
      maskClosable={!loading}
      centered
      title={(
        <FormSubmitControls
          label={`${recalculate ? 'Recalcular' : 'Agregar'} cargo`}
          controls={controls}
          loading={loading}
        />
      )}
    >
      <Spin tip="Cargando..." spinning={loading}>
        {!hideTable ? (
          <Col span={24}>
            <Col span={24}>
              <Form
                form={formCantidad}
                layout="vertical"
                name="form_cantidad"
                initialValues={{ cantidad: 1 }}
                className="without-feedback"
                onFinish={() => (!loading ? agregarCargo(selectedCargo) : null)}
              >
                <Row>
                  <Col xs={24} sm={24} md={8}>
                    <NumericInput
                      name="cantidad"
                      label="Cantidad"
                      disabled={selectedCargo.tipo_de_aplicacion !== aplicacionesCargosEnum.FIJO
                        || !selectedRowKeys.length
                        || selectedCargo.es_cargo_unico
                        || loading}
                      commas
                    />
                  </Col>
                </Row>
              </Form>
            </Col>
            <Table
              cols={columns}
              data={data?.results || []}
              rowSelection={rowSelection}
              onRow={(record) => ({
                onClick: () => {
                  setSelectedRowKeys([record.id]);
                  setSelectedCargo(record);
                },
                onDoubleClick: () => {
                  onFinishSearch(record);
                },
              })}
              allowEdit={false}
              allowAdd={false}
              allowDelete={false}
              allowExport={false}
              rowKey="id"
              hasAllViewPermission
              pagination={false}
              count={data.count}
              paginationCallback={fetchCargos}
              defaultVisibleSearch
              currentPage={currentPage}
            />
          </Col>
        ) : (
          <Form
            layout="vertical"
            form={form}
            onFinish={() => (!loading ? agregarCargo(selectedCargo) : null)}
          >
            {tipoAplicacion === aplicacionesCargosEnum.CAPTURABLE && (
              <Col span={24}>
                <NumericInput
                  name="importe"
                  rules={rules.importe}
                  label={`Importe (MIN: ${selectedCargo.importe_minimo} - MAX: ${selectedCargo.importe_maximo}`}
                  disabled={loading}
                  decimal
                  required
                  currency
                />
              </Col>
            )}
            {tipoAplicacion === aplicacionesCargosEnum.TARIFA && (
              <>
                <Text>
                  <Row>
                    {`Clave: ${selectedCargo.clave}`}
                  </Row>
                  <Row>
                    {`Descripción: ${selectedCargo.descripcion}`}
                  </Row>
                </Text>
                <Col span={24}>
                  {selectedCargo.tipo_de_dato === tiposDatosEnum.ENTERO && (
                    <NumericInput
                      label={`Introduzca ${selectedCargo.parametro_descripcion}`}
                      name={esTarifaPorConcepto ? 'variable' : 'importe'}
                      disabled={loading}
                      required
                      commas
                    />
                  )}
                  {selectedCargo.tipo_de_dato === tiposDatosEnum.DECIMAL && (
                    <NumericInput
                      decimal
                      label={`Introduzca ${selectedCargo.parametro_descripcion}`}
                      name={esTarifaPorConcepto ? 'variable' : 'importe'}
                      disabled={loading}
                      required
                      commas
                    />
                  )}
                  {selectedCargo.tipo_de_dato === tiposDatosEnum.RANGO_DE_FECHAS && (
                    <>
                      <Col span={24}>
                        <Form.Item
                          label="Fecha inicial"
                          name="fecha_inicial"
                        >
                          <DatePicker
                            placeholder=""
                            format="DD/MM/YYYY"
                            disabled={loading}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          label="Fecha final"
                          name="fecha_final"
                        >
                          <DatePicker
                            placeholder=""
                            format="DD/MM/YYYY"
                            disabledDate={(d) => d.isBefore(form.getFieldValue('fecha_inicial'))}
                            disabled={loading}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                  {esTarifaPorConcepto && (
                    <Form.Item
                      name="importe"
                      rules={rules.required}
                      label="Concepto"
                    >
                      <Select
                        disabled={loading}
                        dataSource={conceptos?.results?.length ? conceptos : conceptosRef.current}
                        render={(e) => `${e.concepto} - ${currencyFormat(e.impuesto_por_pagar || 0)}`}
                        usingExternalDataSource={!conceptos?.results?.length}
                        onClear={() => {
                          if (conceptos.results.length) setConceptos({ results: [] });
                        }}
                        pagination={{
                          getter: getTarifas,
                          setter: (_data) => { conceptosRef.current = _data; },
                          params: { catalogo_de_tarifa: selectedCargo.catalogo_de_tarifa },
                          config: { qName: 'concepto', selfValue: concepto, auto: true },
                        }}
                      />
                    </Form.Item>
                  )}
                </Col>
              </>
            )}
            {tipoAplicacion === aplicacionesCargosEnum.FORMULA && (
            <Row gutter={10}>
              <Variables
                catalogoDeVariables={catalogoDeVariables}
                variables={variables.map((e) => e.id)}
                valoresDeVariablesDeSelecion={variablesOptions}
                categoriasDeVariables={categoriasDeVariables}
              />
            </Row>
            )}
            {tipoAplicacion === aplicacionesCargosEnum.MULTIPLO && (
              <>
                <Text>
                  <Row>
                    {`Clave: ${selectedCargo.clave}`}
                  </Row>
                  <Row>
                    {`Descripción: ${selectedCargo.descripcion}`}
                  </Row>
                </Text>
                <Col span={24}>
                  {[1, 4, 2, 5]
                    .includes(selectedCargo.tipo_de_dato) && (
                    <NumericInput
                      label={`Introduzca ${selectedCargo.parametro_descripcion}`}
                      name={esTarifaPorConcepto ? 'variable' : 'importe'}
                      disabled={loading}
                      decimal
                      required
                      commas
                    />
                  )}
                </Col>
              </>
            )}
            {selectedCargo?.es_editable_fecha_de_vencimiento && (
              <Col span={24}>
                <Form.Item
                  label="Fecha de vencimiento"
                  name="fecha_de_vencimiento"
                  rules={rules.required}
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    disabled={loading}
                  />
                </Form.Item>
              </Col>
            )}
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        )}
      </Spin>
    </Modal>
  );
};

ModalAgregarCargos.propTypes = {
  callback: PropTypes.func.isRequired,
  _cargo: PropTypes.shape({
    id: PropTypes.number.isRequired,
    tipo_de_cargo: PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired,
  }),
  recalculate: PropTypes.bool,
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
  padron: PropTypes.shape({
    id: PropTypes.number.isRequired,
    padron: PropTypes.number,
    tipo_de_padron: PropTypes.number,
  }),
};

ModalAgregarCargos.defaultProps = {
  _cargo: null,
  recalculate: false,
  visible: false,
  onCancel: () => {},
  padron: null,
};

export default ModalAgregarCargos;
